import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { Select, TextField } from 'mui-rff';
import JsonView from 'react-json-view';
import { Alert, Box, Drawer, Grid, MenuItem } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import SearchIcon from '@mui/icons-material/Search';

import { Typography } from '@components/styledComponents';

import { breadcrumbsActions } from '@store/breadcrumbs';
import { searchOrder } from '../../payment-search.breadcrumbs';

import {
  paymentSearchActions,
  getOrderDetails,
  selectIsOrderDetailsLoading,
  selectIsPaymentTransactionsLoading,
  selectOrderDetails,
  selectPaymentTransactions,
  getOrderTransactions,
  selectRefundTransactions,
  selectisRefundTransactionsLoading,
  getRefundTransactions,
  getTransactionDetails,
  getRefundDetails,
  selectOrderDetailsError,
  selectPaymentTransactionsError,
  selectRefundTransactionsError,
  selectSearchParams,
} from '../../store/searchOrder.slice';

import ApplicationPageTemplate from '../../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate';
import DataTable from '../../components/DataTable';

import { getColumns } from '../../payment-search.utils';

import {
  SEARCH_TYPES,
  SEARCH_CONFIG,
  REACT_JSON_PROPS,
  SEARCH_DB,
  DATA_TYPE,
} from '../../payment-search.constants';

const initForm = { id: null, type: 'orderId', database: SEARCH_DB.CASHIER };

function SearchForm({
  handleSubmit,
  values,
  invalid,
  isSearchingOrder,
  formErrorMsgs = [],
  setFormErrorMsgs,
}) {
  return (
    <form
      id="searchForm"
      onSubmit={handleSubmit}
      noValidate>
      <Grid
        container
        spacing={2}
        alignItems="center">
        <Grid
          item
          xs={3}>
          <Select
            name="type"
            variant="standard"
            disabled={isSearchingOrder}
            label="Type">
            {Object.keys(SEARCH_CONFIG).map((type) => (
              <MenuItem
                key={type}
                disabled={SEARCH_CONFIG[type].disabled}
                value={type}>
                {SEARCH_CONFIG[type].label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid
          item
          xs={5}>
          <TextField
            required
            name="id"
            label={SEARCH_CONFIG[values.type].label}
            variant="standard"
            InputProps={{ readOnly: isSearchingOrder }}
          />
        </Grid>
        <Grid
          item
          xs={2}>
          <Select
            name="database"
            variant="standard"
            disabled={isSearchingOrder}
            label="Database">
            {Object.values(SEARCH_CONFIG[values.type].searchDBs).map((db) => (
              <MenuItem
                key={db}
                value={db}>
                {db}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid
          item
          xs={2}>
          <LoadingButton
            fullWidth
            size="large"
            variant="contained"
            type="submit"
            disabled={invalid}
            loading={isSearchingOrder}
            startIcon={<SearchIcon />}
            loadingPosition="start">
            Search
          </LoadingButton>
        </Grid>
        {formErrorMsgs &&
          formErrorMsgs.length > 0 &&
          formErrorMsgs.map((msg, index) => (
            <Grid
              item
              key={msg}
              xs={12}>
              <Alert
                onClose={() => {
                  setFormErrorMsgs((messages) => {
                    const errors = [...messages];
                    errors.splice(index, 1);
                    return errors;
                  });
                }}
                severity="error">
                {msg}
              </Alert>
            </Grid>
          ))}
      </Grid>
    </form>
  );
}

export default function SearchOrders({ permissions = {} }) {
  const { orderId: orderIdParam } = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerJson, setDrawerJson] = useState({});
  const [drawerTitle, setDrawerTitle] = useState('');

  const [initFormValues, setInitFormValues] = useState({
    ...initForm,
    id: orderIdParam,
  });

  const searchParams = useSelector(selectSearchParams);
  const isSearchingOrder = useSelector(selectIsOrderDetailsLoading);
  const orderDetails = useSelector(selectOrderDetails);
  const orderDetailsError = useSelector(selectOrderDetailsError);

  const isSearchingTransactions = useSelector(
    selectIsPaymentTransactionsLoading,
  );
  const paymentTransactions = useSelector(selectPaymentTransactions);
  const paymentTransactionsError = useSelector(selectPaymentTransactionsError);

  const isLoadingRefundTransactions = useSelector(
    selectisRefundTransactionsLoading,
  );
  const refundTransactions = useSelector(selectRefundTransactions);
  const refundTransactionsError = useSelector(selectRefundTransactionsError);

  useEffect(() => {
    if (!permissions?.canSearchOrder) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(searchOrder));
  }, [dispatch]);

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }

    setIsDrawerOpen(open);
  };

  const fetchOrderDetails = (orderId) => {
    if (!orderId) return;

    dispatch(getOrderDetails(orderId));
  };

  const fetchPaymentTransactions = (params) => {
    dispatch(getOrderTransactions(params));
  };

  const fetchTransactionDetails = (params) => {
    dispatch(getTransactionDetails(params));
  };

  const fetchRefundTransactions = (params) => {
    dispatch(getRefundTransactions(params));
  };

  const fetchRefundDetails = (params) => {
    dispatch(getRefundDetails(params));
  };

  const onSubmit = (values) => {
    dispatch(paymentSearchActions.resetOrder());
    dispatch(paymentSearchActions.resetTransactions());
    dispatch(paymentSearchActions.resetRefunds());
    dispatch(paymentSearchActions.setSearchParams(values));

    if (!values.id || !values.database) return;

    const id = values.id.trim().replace(/^\s*["'](.*)["']\s*$/, '$1');
    const database = values.database.toLowerCase();

    switch (values.type) {
      case SEARCH_TYPES.ORDER_ID: {
        const orderId = id;

        fetchOrderDetails(orderId);
        fetchPaymentTransactions({
          database,
          params: {
            orderId,
          },
        });
        fetchRefundTransactions({
          database,
          params: {
            orderId,
          },
        });
        break;
      }
      case SEARCH_TYPES.PAYMENT_REF_NUM: {
        fetchTransactionDetails({
          database,
          params: {
            paymentReferenceNumber: id,
          },
        });

        break;
      }
      case SEARCH_TYPES.AUTH_PSP_REF_ID: {
        fetchTransactionDetails({
          database,
          params: {
            authPspReferenceId: id,
          },
        });
        break;
      }
      case SEARCH_TYPES.PSP_REF_ID: {
        fetchTransactionDetails({
          database,
          params: {
            pspReferenceId: id,
          },
        });
        break;
      }
      case SEARCH_TYPES.REFUND_REFERENCE_NUM: {
        fetchRefundDetails({
          database,
          params: {
            refundReferenceNumber: id,
          },
        });
        break;
      }
      default:
        break;
    }
  };

  const validate = (values) => {
    if (!values.id) return { [FORM_ERROR]: 'Search value is required' };
    return {};
  };

  const exploreOrder = (orderId) => {
    const values = {
      id: orderId,
      type: 'orderId',
      database: SEARCH_DB.CASHIER,
    };
    setInitFormValues(values);
    onSubmit(values);
  };

  return (
    <ApplicationPageTemplate pageReady>
      <Form
        onSubmit={onSubmit}
        initialValues={initFormValues}
        validate={validate}
        render={({ handleSubmit, values, invalid }) => (
          <>
            <SearchForm
              handleSubmit={handleSubmit}
              values={values}
              invalid={invalid}
              isSearchingOrder={isSearchingOrder}
            />
            <DataTable
              title="Order Details"
              columns={getColumns(DATA_TYPE.ORDER, searchParams?.database)}
              data={orderDetails}
              isLoading={isSearchingOrder}
              errorMessage={orderDetailsError}
              setIsDrawerOpen={setIsDrawerOpen}
              setDrawerJson={setDrawerJson}
              setDrawerTitle={setDrawerTitle}
            />
            <DataTable
              title="Payment Transactions"
              columns={getColumns(
                DATA_TYPE.PAYMENT_TRANSACTION,
                searchParams?.database,
              )}
              data={paymentTransactions}
              isLoading={isSearchingTransactions}
              errorMessage={paymentTransactionsError}
              setIsDrawerOpen={setIsDrawerOpen}
              setDrawerJson={setDrawerJson}
              setDrawerTitle={setDrawerTitle}
              exploreOrder={exploreOrder}
            />
            <DataTable
              title="Refund Transactions"
              columns={getColumns(
                DATA_TYPE.REFUND_TRANSACTION,
                searchParams?.database,
              )}
              data={refundTransactions}
              isLoading={isLoadingRefundTransactions}
              errorMessage={refundTransactionsError}
              setIsDrawerOpen={setIsDrawerOpen}
              setDrawerJson={setDrawerJson}
              setDrawerTitle={setDrawerTitle}
              exploreOrder={exploreOrder}
            />
          </>
        )}
      />
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={toggleDrawer(false)}>
        <Box
          style={{ padding: 20 }}
          minWidth="500px"
          maxWidth="500px">
          <Typography variant="formHeader">{drawerTitle}</Typography>
          <JsonView
            src={drawerJson}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...REACT_JSON_PROPS}
          />
        </Box>
      </Drawer>
    </ApplicationPageTemplate>
  );
}
